import { useRef } from 'react';
import { ALTITUDES, ASSURANCE, DATA_FORMATS, MANIFESTO, PRINCIPLES } from '../content';
import { useScrollProgress } from '../hooks';
import { imgUrl, srcSet } from '../theme';
import Reveal from './Reveal';
import { LogoMark } from './Logo';
import { Check, Plane } from './Icons';
import './Sections.css';

/* ---------------------------------------------------------- Data marquee */

export function DataMarquee() {
  const items = [...DATA_FORMATS, ...DATA_FORMATS];
  return (
    <section className="tone-dark marquee" data-nav="dark" aria-labelledby="marquee-title">
      <div className="container">
        <h2 id="marquee-title" className="eyebrow marquee__title">
          We speak aviation
        </h2>
      </div>
      <div className="marquee__viewport">
        <ul className="marquee__track">
          {items.map((item, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={i} aria-hidden={i >= DATA_FORMATS.length || undefined}>
              {item}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------ Principles */

const METAR_TOKENS = [
  ['KSFO', 'Station'],
  ['011856Z', 'Time'],
  ['28012KT', 'Wind'],
  ['10SM', 'Visibility'],
  ['FEW015', 'Clouds'],
  ['18/12', 'Temp/dew'],
  ['A3002', 'Altimeter'],
];

const PRINCIPLE_VISUALS = {
  cockpit: (
    <div className="pv-modes">
      {['day', 'night'].map((mode) => (
        <div key={mode} className={`pv-mode pv-mode--${mode}`}>
          <span className="pv-mode__label mono">{mode === 'day' ? 'Day' : 'Night'}</span>
          <div className="pv-mode__read">
            <b>12,500</b>
            <small className="mono">ALT FT</small>
          </div>
          <div className="pv-mode__read">
            <b>142</b>
            <small className="mono">GS KT</small>
          </div>
        </div>
      ))}
    </div>
  ),
  offline: (
    <div className="pv-sync">
      <div className="pv-sync__bar">
        <Plane width={18} height={18} />
        <span>Offline · FL410</span>
        <span className="pv-sync__pill mono">3 queued</span>
      </div>
      <ul>
        {['Logbook entry', 'Fuel log', 'Squawk report'].map((item) => (
          <li key={item}>
            <Check width={16} height={16} />
            <span>{item}</span>
            <span className="mono">saved on device</span>
          </li>
        ))}
      </ul>
    </div>
  ),
  data: (
    <div className="pv-metar">
      {METAR_TOKENS.map(([token, label], i) => (
        <span key={token} className="pv-tok" style={{ '--i': i }}>
          <b className="mono">{token}</b>
          <i>{label}</i>
        </span>
      ))}
    </div>
  ),
  ship: (
    <ol className="pv-pipe">
      {[
        ['Build', '42 s', 'done'],
        ['Tests', '1,284 passed', 'done'],
        ['Beta', '10% of pilots', 'live'],
        ['Release', 'Store review', 'next'],
      ].map(([step, detail, state]) => (
        <li key={step} className={`is-${state}`}>
          <span className="pv-pipe__mark">{state === 'done' && <Check width={13} height={13} />}</span>
          <span>{step}</span>
          <span className="mono">{detail}</span>
        </li>
      ))}
    </ol>
  ),
};

export function Principles() {
  return (
    <section className="section tone-light principles" data-nav="light">
      <div className="container">
        <Reveal className="section-head">
          <span className="eyebrow">How we build</span>
          <h2 className="h2">
            Built like flight software.
            <br /> Shipped like a startup.
          </h2>
        </Reveal>
        <div className="principles__grid">
          {PRINCIPLES.map((p, i) => (
            <Reveal key={p.id} as="article" className="pcard" delay={(i % 2) * 100}>
              <div className="pcard__visual" aria-hidden="true">
                {PRINCIPLE_VISUALS[p.id]}
              </div>
              <h3 className="h3">{p.title}</h3>
              <p className="body-2">{p.body}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------- Manifesto */

export function Manifesto() {
  const ref = useRef(null);
  const progress = useScrollProgress(ref, { start: 0.88, end: 0.5 });
  const words = MANIFESTO.text.split(' ');

  return (
    <section className="section tone-light manifesto" data-nav="light">
      <div className="container">
        <figure ref={ref} className="manifesto__quote" style={{ '--p': progress, '--n': words.length }}>
          <blockquote>
            <p>
              {words.map((word, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <span key={i} style={{ '--i': i }}>
                  {word}{' '}
                </span>
              ))}
            </p>
          </blockquote>
          <figcaption>
            <LogoMark size={22} />
            <span>
              <b>{MANIFESTO.signature}</b> — {MANIFESTO.aside}
            </span>
          </figcaption>
        </figure>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------- Altitudes */

export function Altitudes() {
  return (
    <section id="stack" className="section tone-dark altitudes" data-nav="dark">
      <div className="container">
        <Reveal className="section-head section-head--center">
          <span className="eyebrow">Stack</span>
          <h2 className="h2">One team for every altitude.</h2>
          <p className="lead">
            From a 1.4-inch watch face to the cloud, we work across the whole stack — and pick the right tools for the
            mission, not the trendiest ones.
          </p>
        </Reveal>
        <div className="altitudes__grid">
          {ALTITUDES.map((a, i) => (
            <Reveal key={a.level} className="altitudes__col" delay={i * 90}>
              <span className="altitudes__idx mono">0{i + 1}</span>
              <h3 className="altitudes__level">{a.level}</h3>
              <p className="altitudes__caption">{a.caption}</p>
              <ul className="altitudes__tech">
                {a.tech.map((t) => (
                  <li key={t}>{t}</li>
                ))}
              </ul>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------- Assurance */

export function Assurance({ theme }) {
  return (
    <section className="section tone-dark assurance" data-nav="dark">
      <div className="container">
        <Reveal className="section-head">
          <span className="eyebrow">Reliability</span>
          <h2 className="h2">
            Reliability isn’t a feature.
            <br /> It’s the airframe.
          </h2>
        </Reveal>
        <div className="assurance__grid">
          {ASSURANCE.map((a, i) => {
            const name = `card-${theme.cards[a.image]}`;
            return (
              <Reveal key={a.title} as="article" className="acard" delay={i * 100}>
                <div className="acard__media">
                  <img
                    src={imgUrl(name, 720)}
                    srcSet={srcSet(name, [720, 1280])}
                    sizes="(max-width: 900px) 92vw, 380px"
                    loading="lazy"
                    decoding="async"
                    alt=""
                  />
                </div>
                <h3 className="h3">{a.title}</h3>
                <p className="body-2">{a.body}</p>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
